import React, { useState } from 'react';
import { Download, Link, AlertCircle, Loader2 } from 'lucide-react';

export default function App() {
  const [url, setUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const extractTourId = (komootUrl) => {
    const match = komootUrl.match(/tour\/(\d+)/);
    return match ? match[1] : null;
  };

  const fetchKomootData = async (tourId) => {
    try {
      const response = await fetch(`https://www.komoot.com/api/v007/tours/${tourId}?_embed=coordinates`);
      
      if (!response.ok) {
        throw new Error('Impossible de récupérer les données du parcours');
      }
      
      const data = await response.json();
      return data;
    } catch (err) {
      throw new Error('Erreur lors de la récupération des données: ' + err.message);
    }
  };

  const convertToGPX = (tourData) => {
    const coordinates = tourData._embedded?.coordinates?.items || [];
    const name = tourData.name || 'Parcours Komoot';
    const date = new Date().toISOString();
    
    let gpxContent = `<?xml version="1.0" encoding="UTF-8"?>
<gpx version="1.1" creator="Komoot to GPX Converter" 
     xmlns="http://www.topografix.com/GPX/1/1"
     xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance"
     xsi:schemaLocation="http://www.topografix.com/GPX/1/1 http://www.topografix.com/GPX/1/1/gpx.xsd">
  <metadata>
    <name>${escapeXml(name)}</name>
    <time>${date}</time>
  </metadata>
  <trk>
    <name>${escapeXml(name)}</name>
    <trkseg>
`;

    coordinates.forEach(coord => {
      const lat = coord.lat;
      const lng = coord.lng;
      const alt = coord.alt || 0;
      
      gpxContent += `      <trkpt lat="${lat}" lon="${lng}">
        <ele>${alt}</ele>
      </trkpt>
`;
    });

    gpxContent += `    </trkseg>
  </trk>
</gpx>`;

    return gpxContent;
  };

  const escapeXml = (str) => {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&apos;');
  };

  const downloadGPX = (gpxContent, filename) => {
    const blob = new Blob([gpxContent], { type: 'application/gpx+xml' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const handleConvert = async () => {
    setError('');
    setSuccess('');
    
    if (!url.trim()) {
      setError('Veuillez entrer une URL Komoot');
      return;
    }

    const tourId = extractTourId(url);
    if (!tourId) {
      setError('URL Komoot invalide. Format attendu: https://www.komoot.com/tour/123456789');
      return;
    }

    setLoading(true);

    try {
      const tourData = await fetchKomootData(tourId);
      const gpxContent = convertToGPX(tourData);
      const filename = `komoot_${tourId}.gpx`;
      
      downloadGPX(gpxContent, filename);
      setSuccess(`Fichier GPX téléchargé avec succès: ${filename}`);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-50 p-8">
      <div className="max-w-2xl mx-auto">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="bg-green-500 p-3 rounded-lg">
              <Link className="text-white" size={28} />
            </div>
            <h1 className="text-3xl font-bold text-gray-800">
              Komoot → GPX
            </h1>
          </div>

          <p className="text-gray-600 mb-6">
            Convertissez facilement vos parcours Komoot en fichiers GPX pour les utiliser 
            avec d'autres applications GPS.
          </p>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                URL du parcours Komoot
              </label>
              <input
                type="text"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                placeholder="https://www.komoot.com/tour/123456789"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent outline-none"
                disabled={loading}
              />
              <p className="text-xs text-gray-500 mt-1">
                Exemple: https://www.komoot.com/tour/987654321
              </p>
            </div>

            <button
              onClick={handleConvert}
              disabled={loading}
              className="w-full bg-green-500 hover:bg-green-600 disabled:bg-gray-400 text-white font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="animate-spin" size={20} />
                  Conversion en cours...
                </>
              ) : (
                <>
                  <Download size={20} />
                  Convertir et télécharger
                </>
              )}
            </button>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="text-red-500 flex-shrink-0" size={20} />
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-700 text-sm font-medium">{success}</p>
              </div>
            )}
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200">
            <h3 className="font-semibold text-gray-800 mb-2">
              Comment ça marche ?
            </h3>
            <ol className="text-sm text-gray-600 space-y-2">
              <li>1. Ouvrez un parcours sur Komoot et copiez l'URL</li>
              <li>2. Collez l'URL dans le champ ci-dessus</li>
              <li>3. Cliquez sur "Convertir et télécharger"</li>
              <li>4. Le fichier GPX sera téléchargé automatiquement</li>
            </ol>
          </div>
        </div>
      </div>
    </div>
  );
}